// Mapa de navegação por perfil.
//
// Decisões registradas em docs/validacao-prototipo.md §5.6 e §5.12:
// - Dashboards: Técnico vê os quatro, com os dados restritos às áreas de que é
//   membro; Administrador vê tudo.
// - Administração: só Administrador.
// Quem barra de verdade é o guard do backend; isto aqui só decide o menu.

import {
  Boxes,
  Building2,
  CalendarDays,
  CheckCircle2,
  Clock,
  FolderKanban,
  Gauge,
  Inbox,
  LayoutDashboard,
  ListChecks,
  Network,
  PlusCircle,
  Route,
  ScrollText,
  Smile,
  Tags,
  Timer,
  TrendingUp,
  UserCheck,
  UserCog,
  Users,
  type LucideIcon,
} from 'lucide-react'
import type { UserRole } from '@c1hd/shared'

// Os ícones são importados um a um de propósito: `import * as icons` arrasta a
// biblioteca inteira do lucide para o bundle (~1,4 MB).
export interface NavItem {
  to: string
  label: string
  icon: LucideIcon
}

export interface NavGroup {
  title: string
  items: NavItem[]
}

const CHAMADOS: NavGroup = {
  title: 'Chamados',
  items: [
    { to: '/painel/fila', label: 'Fila de chamados', icon: Inbox },
    { to: '/painel/meus', label: 'Meus atendimentos', icon: UserCheck },
    { to: '/painel/aguardando', label: 'Aguardando cliente', icon: Clock },
    { to: '/painel/encerrados', label: 'Encerrados', icon: CheckCircle2 },
  ],
}

const INDICADORES: NavGroup = {
  title: 'Indicadores',
  items: [
    { to: '/gestao/dashboard', label: 'Visão geral', icon: LayoutDashboard },
    { to: '/gestao/sla', label: 'SLA e escalonamento', icon: Timer },
    { to: '/gestao/satisfacao', label: 'Satisfação', icon: Smile },
    { to: '/gestao/equipe', label: 'Equipe e carga', icon: Users },
  ],
}

const ADMINISTRACAO: NavGroup = {
  title: 'Administração',
  items: [
    { to: '/admin/usuarios', label: 'Usuários', icon: UserCog },
    { to: '/admin/empresas', label: 'Empresas', icon: Building2 },
    { to: '/admin/projetos', label: 'Projetos', icon: FolderKanban },
    { to: '/admin/sistemas', label: 'Sistemas', icon: Boxes },
    { to: '/admin/categorias', label: 'Categorias', icon: Tags },
    { to: '/admin/assuntos', label: 'Assuntos', icon: ListChecks },
    { to: '/admin/areas', label: 'Áreas e equipes', icon: Network },
    { to: '/admin/roteamento', label: 'Regras de roteamento', icon: Route },
    { to: '/admin/sla', label: 'Políticas de SLA', icon: Gauge },
    { to: '/admin/calendarios', label: 'Calendários', icon: CalendarDays },
    { to: '/admin/escalonamento', label: 'Escalonamento', icon: TrendingUp },
    { to: '/admin/auditoria', label: 'Auditoria', icon: ScrollText },
  ],
}

/** Portal do cliente: navegação no topo, não na lateral. */
export const CLIENT_NAV: NavItem[] = [
  { to: '/chamados/novo', label: 'Abrir chamado', icon: PlusCircle },
  { to: '/chamados', label: 'Meus chamados', icon: Inbox },
]

export function navForRole(role: UserRole): NavGroup[] {
  switch (role) {
    case 'administrador':
      return [CHAMADOS, INDICADORES, ADMINISTRACAO]
    case 'tecnico':
      return [CHAMADOS, INDICADORES]
    case 'cliente':
      return []
  }
}

export const ROLE_LABEL: Record<UserRole, string> = {
  cliente: 'Cliente',
  tecnico: 'Técnico de suporte',
  administrador: 'Administrador',
}

/** Iniciais para o avatar: primeiro e último nome. */
export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  const first = parts[0]?.[0] ?? ''
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : ''
  return (first + last).toUpperCase()
}
